import type { IUserPermissions } from '../sharedTypes';

interface IPermissions {
  id: number;
  name: string;
  paid_role: boolean;
  user_type: string;
  feature_groups: {
    name: string;
    permissions: string[];
  }[];
}

const decodeUserPermissionsMeta = (encoded: string) => {
  let parsed: unknown = JSON.parse(atob(encoded));
  if (typeof parsed === 'string') {
    parsed = JSON.parse(parsed);
  }
  return parsed as { roles: IPermissions[] };
};

export const getPermissions = () => {
  const meta = document.querySelector('meta[name="user-permissions"]');
  const encodedUserPermissions = meta?.getAttribute('content') ?? meta?.getAttribute('value') ?? '';

  if (!encodedUserPermissions) {
    throw new Error('user-permissions meta tag is missing or empty');
  }

  return decodeUserPermissionsMeta(encodedUserPermissions);
};

export const formatUserPermissions = (): IUserPermissions => {
  const allRoles: Record<string, Record<string, boolean>> = {};
  getPermissions().roles.forEach((role: IPermissions) =>
    role.feature_groups.forEach((group) => {
      if (!allRoles[group.name]) {
        allRoles[group.name] = {};
      }
      group.permissions.forEach((permission) => {
        allRoles[group.name][permission] = true;
      });
    })
  );
  const hasRecruitmentTeamEnabled = getPermissions().roles.some(
    (user) => user.recruitment_team_enabled
  );
  const paidUser = getPermissions().roles.some((user) => user.paid_role);
  allRoles.paid_user = paidUser;
  allRoles.recruitment_team_enabled = hasRecruitmentTeamEnabled;
  allRoles.user_type = getPermissions().roles[0]?.user_type;
  return allRoles;
};

export const hasJobCreationPermissions = (userPermissions?: IUserPermissions): boolean =>
  Boolean(userPermissions?.Jobs?.['Create Job from Template']) ||
  Boolean(userPermissions?.Jobs?.['Create Job by Clone']) ||
  Boolean(userPermissions?.Jobs?.['Create Job from Blank']);

// The frontend half of the Job Creation Talent Matching three-way gate (UIUX-4251/4252) —
// the `agentic` AwesomeACL permission, seeded as `Agentic Candidate Sourcing` under the
// `Full Search` feature group (db/seeds/permissions.yml). Callers must still check the
// `jobCreationTalentMatchingEnabled` feature flag and entity `candidate_sourcing` flag
// (via useFeatureFlag) separately — this alone is not the full gate.
export const hasAgenticPermission = (userPermissions?: IUserPermissions): boolean =>
  !!userPermissions?.['Full Search']?.['Agentic Candidate Sourcing'];

// Display names of the approval-forms permission set, i.e. Requisition::ACCESS_PERMISSIONS
// rendered as the names the frontend can see. Any of them grants READ access to requisitions
// (Api::V4::RequisitionsController's READ_ACTIONS); the writes are gated more narrowly.
// spec/rules/approval_forms_gate_consistency_spec.rb keeps this list honest against
// db/seeds/permissions.yml — a UI gate broader than the server's is what produced UNS-7479.
export const APPROVAL_FORMS_PERMISSION_NAMES = [
  'Create / Edit Approval Forms (HR use)',
  'Edit Previously Submitted Form',
  "See Forms I'm Assigned To or Approving",
  'Submit Approval Form'
] as const;

export const hasApprovalFormsAccess = (userPermissions?: IUserPermissions): boolean =>
  APPROVAL_FORMS_PERMISSION_NAMES.some((name) => !!userPermissions?.['Approval Forms']?.[name]);

export const formatUserPermissionsAsArray = () => {
  const rolesArray: string[] = [];
  getPermissions().roles.forEach((role: IPermissions) =>
    role.feature_groups.forEach((group) => {
      if (rolesArray.includes(group.name)) return;
      group.permissions.forEach((permission) => rolesArray.push(permission));
    })
  );
  return rolesArray;
};
